/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Directive,
  DoCheck,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Renderer2,
  Self,
} from '@angular/core';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {FormControl, FormGroupDirective, NgControl, NgForm} from '@angular/forms';
import {Platform, getSupportedInputTypes} from '@angular/cdk/platform';
import {getMdInputUnsupportedTypeError} from './input-errors';
import {
  defaultErrorStateMatcher,
  ErrorOptions,
  ErrorStateMatcher,
  MD_ERROR_GLOBAL_OPTIONS
} from '@angular/material/core';
import {Subject} from 'rxjs/Subject';
import {MdFormFieldControl} from '@angular/material/form-field';

// Invalid input type. Using one of these will throw an MdInputUnsupportedTypeError.
const /** @type {?} */ MD_INPUT_INVALID_TYPES = [
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit'
];

let /** @type {?} */ nextUniqueId = 0;
/**
 * Directive that allows a native input to work inside a `MdFormField`.
 */
export class MdInput implements MdFormFieldControl<any>, OnChanges, OnDestroy, DoCheck {
/**
 * Variables used as cache for getters and setters.
 */
protected _type = 'text';
  protected _disabled = false;
  protected _required = false;
  protected _id: string;
  protected _uid = `md-input-${nextUniqueId++}`;
  protected _errorOptions: ErrorOptions;
  protected _previousNativeValue = this.value;
/**
 * Whether the input is focused.
 */
focused = false;
/**
 * Whether the input is in an error state.
 */
errorState = false;
/**
 * The aria-describedby attribute on the input for improved a11y.
 */
_ariaDescribedby: string;
/**
 * Stream that emits whenever the state of the input changes such that the wrapping `MdFormField`
 * needs to run change detection.
 */
stateChanges = new Subject<void>();
/**
 * Whether the element is disabled.
 * @return {?}
 */
get disabled() { return this.ngControl ? this.ngControl.disabled : this._disabled; }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: any) { this._disabled = coerceBooleanProperty(value); }
/**
 * Unique id of the element.
 * @return {?}
 */
get id() { return this._id; }
/**
 * @param {?} value
 * @return {?}
 */
set id(value: string) { this._id = value || this._uid; }
/**
 * Placeholder attribute of the element.
 */
placeholder: string = '';
/**
 * Whether the element is required.
 * @return {?}
 */
get required() { return this._required; }
/**
 * @param {?} value
 * @return {?}
 */
set required(value: any) { this._required = coerceBooleanProperty(value); }
/**
 * Input type of the element.
 * @return {?}
 */
get type() { return this._type; }
/**
 * @param {?} value
 * @return {?}
 */
set type(value: string) {
    this._type = value || 'text';
    this._validateType();

    // When using Angular inputs, developers are no longer able to set the properties on the native
    // input element. To ensure that bindings for `type` work, we need to sync the setter
    // with the native property. Textarea elements don't support the type property or attribute.
    if (!this._isTextarea() && getSupportedInputTypes().has(this._type)) {
      this._renderer.setProperty(this._elementRef.nativeElement, 'type', this._type);
    }
  }
/**
 * A function used to control when error messages are shown.
 */
errorStateMatcher: ErrorStateMatcher;
/**
 * The input element's value.
 * @return {?}
 */
get value() { return this._elementRef.nativeElement.value; }
/**
 * @param {?} value
 * @return {?}
 */
set value(value: string) {
    if (value !== this.value) {
      this._elementRef.nativeElement.value = value;
      this.stateChanges.next();
    }
  }

  protected _neverEmptyInputTypes = [
    'date',
    'datetime',
    'datetime-local',
    'month',
    'time',
    'week'
  ].filter(t => getSupportedInputTypes().has(t));
/**
 * @param {?} _elementRef
 * @param {?} _renderer
 * @param {?} _platform
 * @param {?} ngControl
 * @param {?} _parentForm
 * @param {?} _parentFormGroup
 * @param {?} errorOptions
 */
constructor(protected _elementRef: ElementRef,
              protected _renderer: Renderer2,
              protected _platform: Platform,
public ngControl: NgControl,
               protected _parentForm: NgForm,
               protected _parentFormGroup: FormGroupDirective,
                errorOptions: ErrorOptions) {

    // Force setter to be called in case id was not specified.
    this.id = this.id;
    this._errorOptions = errorOptions ? errorOptions : {};
    this.errorStateMatcher = this._errorOptions.errorStateMatcher || defaultErrorStateMatcher;

    // On some versions of iOS the caret gets stuck in the wrong place when holding down the delete
    // key. In order to get around this we need to "jiggle" the caret loose. Since this bug only
    // exists on iOS, we only bother to install the listener on iOS.
    if (_platform.IOS) {
      _renderer.listen(_elementRef.nativeElement, 'keyup', (event: Event) => {
        let el = event.target as HTMLInputElement;
        if (!el.value && !el.selectionStart && !el.selectionEnd) {
          // Note: Just setting `0, 0` doesn't fix the issue. Setting `1, 1` fixes it for the first
          // time that you type text and then hold delete. Toggling to `1, 1` and then back to
          // `0, 0` seems to completely fix it.
          el.setSelectionRange(1, 1);
          el.setSelectionRange(0, 0);
        }
      });
    }
  }
/**
 * @return {?}
 */
ngOnChanges() {
    this.stateChanges.next();
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this.stateChanges.complete();
  }
/**
 * @return {?}
 */
ngDoCheck() {
    if (this.ngControl) {
      // We need to re-evaluate this on every change detection cycle, because there are some
      // error triggers that we can't subscribe to (e.g. parent form submissions). This means
      // that whatever logic is in here has to be super lean or we risk destroying the performance.
      this._updateErrorState();
    } else {
      // When the input isn't used together with `@angular/forms`, we need to check manually for
      // changes to the native `value` property in order to update the floating label.
      this._dirtyCheckNativeValue();
    }
  }
/**
 * Callback for the cases where the focused state of the input changes.
 * @param {?} isFocused
 * @return {?}
 */
_focusChanged(isFocused: boolean) {
    if (isFocused !== this.focused) {
      this.focused = isFocused;
      this.stateChanges.next();
    }
  }
/**
 * @return {?}
 */
_onInput() {
    // This is a noop function and is used to let Angular know whenever the value changes.
    // Angular will run a new change detection each time the `input` event has been dispatched.
    // It's necessary that Angular recognizes the value change, because when floatingLabel
    // is set to false and Angular forms aren't used, the placeholder won't recognize the
    // value changes and will not disappear.
    // Listening to the input event wouldn't be necessary when the input is using the
    // FormsModule or ReactiveFormsModule, because Angular forms also listens to input events.
  }
/**
 * Re-evaluates the error state. This is only relevant with \@angular/forms.
 * @return {?}
 */
protected _updateErrorState() {
    const /** @type {?} */ oldState = this.errorState;
    const /** @type {?} */ ngControl = this.ngControl;
    const /** @type {?} */ parent = this._parentFormGroup || this._parentForm;
    const /** @type {?} */ newState = ngControl && this.errorStateMatcher( /** @type {?} */((ngControl.control as FormControl)), parent);

    if (newState !== oldState) {
      this.errorState = newState;
      this.stateChanges.next();
    }
  }
/**
 * Does some manual dirty checking on the native input `value` property.
 * @return {?}
 */
protected _dirtyCheckNativeValue() {
    const /** @type {?} */ newValue = this.value;

    if (this._previousNativeValue !== newValue) {
      this._previousNativeValue = newValue;
      this.stateChanges.next();
    }
  }
/**
 * Make sure the input is a supported type.
 * @return {?}
 */
protected _validateType() {
    if (MD_INPUT_INVALID_TYPES.indexOf(this._type) > -1) {
      throw getMdInputUnsupportedTypeError(this._type);
    }
  }
/**
 * Checks whether the input type is one of the types that are never empty.
 * @return {?}
 */
protected _isNeverEmpty() {
    return this._neverEmptyInputTypes.indexOf(this._type) > -1;
  }
/**
 * Checks whether the input is invalid based on the native validation.
 * @return {?}
 */
protected _isBadInput() {
    // The `validity` property won't be present on platform-server.
    let /** @type {?} */ validity = ( /** @type {?} */((this._elementRef.nativeElement as HTMLInputElement))).validity;
    return validity && validity.badInput;
  }
/**
 * Determines if the component host is a textarea. If not recognizable it returns false.
 * @return {?}
 */
protected _isTextarea() {
    let /** @type {?} */ nativeElement = this._elementRef.nativeElement;

    // In Universal, we don't have access to `nodeName`, but the same can be achieved with `name`.
    // Note that this shouldn't be necessary once Angular switches to an API that resembles the
    // DOM closer.
    let /** @type {?} */ nodeName = this._platform.isBrowser ? nativeElement.nodeName : nativeElement.name;
    return nodeName ? nodeName.toLowerCase() === 'textarea' : false;
  }
/**
 * @return {?}
 */
get empty(): boolean {
    return !this._isNeverEmpty() &&
        (this.value == null || this.value === '') &&
        // Check if the input contains bad input. If so, we know that it only appears empty because
        // the value failed to parse. From the user's perspective it is not empty.
        // TODO(mmalerba): Add e2e test for bad input case.
        !this._isBadInput();
  }
/**
 * @param {?} ids
 * @return {?}
 */
setDescribedByIds(ids: string[]) { this._ariaDescribedby = ids.join(' '); }
/**
 * @return {?}
 */
focus() { this._elementRef.nativeElement.focus(); }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: `input[mdInput], textarea[mdInput], input[matInput], textarea[matInput]`,
  host: {
    'class': 'mat-input-element mat-form-field-autofill-control',
    // Native input properties that are overwritten by Angular inputs need to be synced with
    // the native input element. Otherwise property bindings for those don't work.
    '[id]': 'id',
    '[placeholder]': 'placeholder',
    '[disabled]': 'disabled',
    '[required]': 'required',
    '[attr.aria-describedby]': '_ariaDescribedby || null',
    '[attr.aria-invalid]': 'errorState',
    '(blur)': '_focusChanged(false)',
    '(focus)': '_focusChanged(true)',
    '(input)': '_onInput()',
  },
  providers: [{provide: MdFormFieldControl, useExisting: MdInput}],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: Renderer2, },
{type: Platform, },
{type: NgControl, decorators: [{ type: Optional }, { type: Self }, ]},
{type: NgForm, decorators: [{ type: Optional }, ]},
{type: FormGroupDirective, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_ERROR_GLOBAL_OPTIONS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'disabled': [{ type: Input },],
'id': [{ type: Input },],
'placeholder': [{ type: Input },],
'required': [{ type: Input },],
'type': [{ type: Input },],
'errorStateMatcher': [{ type: Input },],
};
}

function MdInput_tsickle_Closure_declarations() {
/** @type {?} */
MdInput.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdInput.ctorParameters;
/** @type {?} */
MdInput.propDecorators;
/**
 * Variables used as cache for getters and setters.
 * @type {?}
 */
MdInput.prototype._type;
/** @type {?} */
MdInput.prototype._disabled;
/** @type {?} */
MdInput.prototype._required;
/** @type {?} */
MdInput.prototype._id;
/** @type {?} */
MdInput.prototype._uid;
/** @type {?} */
MdInput.prototype._errorOptions;
/** @type {?} */
MdInput.prototype._previousNativeValue;
/**
 * Whether the input is focused.
 * @type {?}
 */
MdInput.prototype.focused;
/**
 * Whether the input is in an error state.
 * @type {?}
 */
MdInput.prototype.errorState;
/**
 * The aria-describedby attribute on the input for improved a11y.
 * @type {?}
 */
MdInput.prototype._ariaDescribedby;
/**
 * Stream that emits whenever the state of the input changes such that the wrapping `MdFormField`
 * needs to run change detection.
 * @type {?}
 */
MdInput.prototype.stateChanges;
/**
 * Placeholder attribute of the element.
 * @type {?}
 */
MdInput.prototype.placeholder;
/**
 * A function used to control when error messages are shown.
 * @type {?}
 */
MdInput.prototype.errorStateMatcher;
/** @type {?} */
MdInput.prototype._neverEmptyInputTypes;
/** @type {?} */
MdInput.prototype._elementRef;
/** @type {?} */
MdInput.prototype._renderer;
/** @type {?} */
MdInput.prototype._platform;
/** @type {?} */
MdInput.prototype.ngControl;
/** @type {?} */
MdInput.prototype._parentForm;
/** @type {?} */
MdInput.prototype._parentFormGroup;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
